import React, {useContext, useEffect, useState} from 'react'
import {FormattedDate} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import {CarouselWidgetsContext} from 'components/CarouselWidgets/context/CarouselWidgetsContext'
import {HighlightedTitle} from 'components/Home/HighlightedTitle'
import {useNavigateToSearchWithTrigger} from 'hooks/useNavigateToSearch'
import {getSearchContext} from 'middleware/analytics/selectors'
import {SearchTrigger} from 'modules/searchBox/types'
import {pick} from 'ramda'
import {getDestinationParameter} from 'utils/searchParams'

import {Button} from '@vio/atlas/Button/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {GuestAndRoomText} from '@vio/shared/src/search/GuestAndRoomsText'

import {SimilarHotels} from '../components/SimilarHotels'
import {SeeAllText, TopSection, TopSectionText} from '../components/TopSection'
import {DESTINATION_KEYS, ITINERARY_KEYS} from '../utils/constants'

export const SuggestedHotels = () => {
  const isHydrated = useIsHydrated()

  const {isMobile} = useDeviceLayout()
  const {searchHistory} = useContext(CarouselWidgetsContext)
  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})
  const [alreadyTrackedCarousel, setAlreadyTrackedCarousel] = useState(false)
  const [carouselRequestCompleted, setCarouselRequestCompleted] =
    useState(false)
  const [carouselHasNoOffers, setCarouselHasNoOffers] = useState(false)

  const searchContext = useSelector(getSearchContext)

  const searchItem = searchHistory[0]

  const {
    boundingBox,
    hotelId,
    placeId,
    placeName,
    objectType,
    rooms,
    checkIn,
    checkOut
  } = searchItem || {}

  const baseSearchParameters = {
    rooms,
    checkIn,
    checkOut,
    userSearch: '1' as const,
    homeSearch: '1' as const
  }
  const navigateToSearch = useNavigateToSearchWithTrigger(
    SearchTrigger.ContinueSearchWidget
  )

  const dispatch = useDispatch()

  const trackingPayload = {
    component: 'SimilarProperties',
    team: Team.Retention
  }

  const onHotelClick = ({hotelId}: {hotelId: string}) =>
    // bookingHint=1 (show date confirmation tooltip) is used to open the search box for mobile and date confirmation tooltip on desktop
    // it was added as part of this task: https://app.shortcut.com/findhotel/story/154347/update-landing-experience-for-users-with-no-dates-default-dates-widget-traffic
    navigateToSearch({...baseSearchParameters, hotelId, bookingHint: '1'})

  const onSeeAllClick = () => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.CarouselSeeAll,
        action: Action.Clicked,
        ...trackingPayload,
        payload: {
          searchItem: {
            ...(boundingBox && {boundingBox}),
            ...(hotelId && {hotelId}),
            ...(placeId && {placeId}),
            checkIn,
            checkOut,
            rooms
          }
        }
      })
    )

    const destinationParameters = getDestinationParameter(
      objectType,
      hotelId ?? placeId ?? placeName ?? boundingBox
    )
    navigateToSearch({...baseSearchParameters, ...destinationParameters})
  }

  const handleOnRequestComplete = (hotelIds: string[]) => {
    setCarouselRequestCompleted(true)
    if (hotelIds.length === 0) {
      setCarouselHasNoOffers(true)
    }
  }

  // Track the carousel "Displayed" event only when the request has completed
  // and returned hotel results. This prevents firing the event for empty carousels
  // that will render null, ensuring tracking accuracy.
  useEffect(() => {
    if (
      inView &&
      !alreadyTrackedCarousel &&
      carouselRequestCompleted &&
      !carouselHasNoOffers
    ) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Carousel,
          action: Action.Displayed,
          ...trackingPayload,
          payload: {
            ...(boundingBox && {boundingBox}),
            ...(hotelId && {hotelId}),
            ...(placeId && {placeId}),
            totalNumber: searchHistory?.length,
            searchItem: pick(
              [...DESTINATION_KEYS, ...ITINERARY_KEYS],
              searchItem
            )
          },
          analyticsContext: {
            [AnalyticsContext.SearchContext]: searchContext
          }
        })
      )
      setAlreadyTrackedCarousel(true)
    }
  }, [
    inView,
    searchContext,
    dispatch,
    alreadyTrackedCarousel,
    setAlreadyTrackedCarousel,
    carouselRequestCompleted,
    carouselHasNoOffers
  ])

  if (
    !isHydrated ||
    !searchItem ||
    (carouselRequestCompleted && carouselHasNoOffers)
  ) {
    return null
  }

  return (
    <div ref={ref} className="chromatic-ignore">
      <TopSection className={cx(isMobile && '--mobile')}>
        <TopSectionText>
          <Text variant="titleXL">
            <HighlightedTitle source="SuggestedHotels" />
          </Text>

          <Box
            display="flex"
            alignItems="center"
            gap="s200"
            marginBottom="s300"
            desktopMd={{marginBottom: 's500'}}
          >
            <Text colorPath="content.neutral.secondary" variant="labelS">
              <FormattedDate
                value={dateStringToMiddayDate(checkIn)}
                month="short"
                day="2-digit"
              />
              {' - '}
              <FormattedDate
                value={dateStringToMiddayDate(checkOut)}
                month="short"
                day="2-digit"
              />
            </Text>
            <Text colorPath="content.neutral.secondary" variant="bodyS">
              ·
            </Text>
            <Text colorPath="content.neutral.secondary" variant="bodyS">
              <GuestAndRoomText rooms={rooms} />
            </Text>
          </Box>
        </TopSectionText>
        {!isMobile && (
          <Button variant="tertiary" onClick={onSeeAllClick}>
            <SeeAllText>
              <FormattedMessageWrapper
                id="hotelCard.seeAll"
                defaultMessage="See all"
              />
            </SeeAllText>
          </Button>
        )}
      </TopSection>
      <SimilarHotels
        boundingBox={boundingBox}
        hotelId={hotelId}
        placeId={placeId}
        address={placeName}
        rooms={rooms}
        checkIn={checkIn}
        checkOut={checkOut}
        trackingPayload={trackingPayload}
        onHotelClick={onHotelClick}
        hasSidePaddings={isMobile}
        parentId="suggested-hotel"
        includeSameProperty
        onRequestComplete={handleOnRequestComplete}
      />
      {isMobile && (
        <Box padding="s500" paddingBottom="s000">
          <Button variant="tertiary" fullWidth onClick={onSeeAllClick}>
            <SeeAllText>
              <FormattedMessageWrapper
                id="hotelCard.seeAll"
                defaultMessage="See all"
              />
            </SeeAllText>
          </Button>
        </Box>
      )}
    </div>
  )
}
